import React, { useState } from 'react';
import {
  Typography,
  Container,
  Paper,
  Box,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack,
  Pagination,
  Button,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetCustomerOrders } from '../../hooks/customer/useGetCustomerOrders';
import { useCancelCustomerOrder } from '../../hooks/customer/useCancelCustomerOrder';
import { useResumePayment } from '../../hooks/customer/useResumePayment';
import CancelOrderDialog from '../../components/customer/CancelOrderDialog';
import InvoiceDownloadButton from '../../components/customer/InvoiceDownloadButton';
import ExpiredOrderBanner from '../../components/customer/ExpiredOrderBanner';
import { isExpiredOrder } from '../../utils/orderHelpers';
import { formatMoney } from '../../utils/money';
import {
  ShoppingBag,
  LocalShipping,
  Inventory2,
  ReceiptLong,
  CalendarMonth,
} from '@mui/icons-material';

const STATUS_COLOR = {
  pending: 'warning',
  processing: 'info',
  confirmed: 'info',
  shipped: 'primary',
  delivered: 'success',
  completed: 'success',
  cancelled: 'error',
  refunded: 'default',
};

const OrderCard = ({ order, lng, onView, onCancel, onResumePayment, isResuming, t }) => {
  const theme = useTheme();
  const itemsCount = order.items?.length || 0;
  const previewItems = order.items?.slice(0, 3) || [];
  const shippingName =
    order.shipping_method?.name?.[lng] || order.shipping_method?.name?.en || '';
  const dateValue = order.placed_at || order.created_at;
  const formattedDate = dateValue
    ? new Date(dateValue).toLocaleDateString(lng === 'ar' ? 'ar-EG' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
  const statusColor = STATUS_COLOR[order.status] || 'default';
  const currency = order.currency || 'EGP';
  const discount = Number(order.discount_total) || 0;

  return (
    <Card
      sx={{
        mb: 2.5,
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.25s ease',
        '&:hover': {
          boxShadow: 6,
          borderColor: theme.palette.primary.main,
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        {isExpiredOrder(order) && <ExpiredOrderBanner order={order} />}
        {/* Header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
          sx={{ mb: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${theme.palette.primary.main}15`,
                color: 'primary.main',
              }}
            >
              <ReceiptLong />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                #{order.order_number}
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CalendarMonth sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formattedDate}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Chip
            label={t(`orders.status.${order.status}`, order.status)}
            color={statusColor}
            size="small"
            sx={{ fontWeight: 600, textTransform: 'capitalize' }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Items preview */}
        <Stack spacing={1.2} sx={{ mb: 2 }}>
          {previewItems.map((item) => {
            const name =
              item.name_snapshot?.[lng] || item.name_snapshot?.en || '—';
            return (
              <Stack
                key={item.id}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ gap: 2 }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Inventory2 fontSize="small" sx={{ color: 'text.secondary' }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('orders.qty', 'Qty')}: {item.qty}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="body2" fontWeight={600} sx={{ flexShrink: 0 }}>
                  {formatMoney(item.line_total, currency)}
                </Typography>
              </Stack>
            );
          })}
          {itemsCount > previewItems.length && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
              +{itemsCount - previewItems.length} {t('orders.moreItems', 'more')}
            </Typography>
          )}
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {/* Footer: shipping + total */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1.5}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {shippingName && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocalShipping sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {shippingName}
                </Typography>
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
              {itemsCount} {t('orders.items', 'items')}
              {discount > 0 && (
                <>
                  {' · '}
                  <Box component="span" sx={{ color: 'success.main', fontWeight: 600 }}>
                    -{formatMoney(discount, currency)}
                  </Box>
                </>
              )}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('orders.total', 'Total')}
            </Typography>
            <Typography variant="h6" color="primary.main" fontWeight={800}>
              {formatMoney(order.grand_total, currency)}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ mt: 2 }}
        >
          {order.status === 'pending' && onResumePayment && (
            <Button
              size="small"
              color="primary"
              variant="contained"
              disabled={isResuming}
              onClick={() => onResumePayment(order)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {isResuming
                ? t('common.loading', 'Loading...')
                : t('orders.resumePayment', 'Resume payment')}
            </Button>
          )}
          {order.status === 'pending' && onCancel && (
            <Button
              size="small"
              color="error"
              variant="outlined"
              onClick={() => onCancel(order)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('orders.cancel', 'Cancel order')}
            </Button>
          )}
          {['delivered', 'shipped'].includes(order.status) && (
            <Button
              size="small"
              variant="outlined"
              component="a"
              href={`/account/orders/${order.id}/return`}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('orders.requestReturn', 'Request return')}
            </Button>
          )}
          <InvoiceDownloadButton orderId={order.id} />
          {onView && (
            <Button
              size="small"
              onClick={() => onView(order)}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              {t('orders.viewDetails', 'View details')}
            </Button>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const Orders = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [cancelTarget, setCancelTarget] = useState(null);
  const { orders, meta, isLoading } = useGetCustomerOrders({ page });
  const cancelMutation = useCancelCustomerOrder();
  const { resume: resumePayment, isLoading: isResumingPayment } = useResumePayment();
  const lng = i18n.language || 'en';
  const lastPage = meta?.last_page || 1;
  const total = meta?.total ?? orders?.length ?? 0;

  const handleCancelConfirm = (reason) => {
    if (!cancelTarget) return;
    cancelMutation.mutate(
      { id: cancelTarget.id, reason },
      {
        onSuccess: () => setCancelTarget(null),
      }
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ShoppingBag fontSize="large" color="primary" />
          <Box>
            <Typography variant="h4" fontWeight={800}>
              {t('orders.title', 'My Orders')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {total} {t('orders.totalOrders', 'orders')}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {orders.length === 0 ? (
        <Paper
          sx={{
            p: { xs: 4, md: 8 },
            textAlign: 'center',
            borderRadius: 3,
            border: (theme) => `2px dashed ${theme.palette.divider}`,
            bgcolor: 'transparent',
          }}
        >
          <ShoppingBag sx={{ fontSize: 72, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            {t('orders.empty', 'You have no orders yet.')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('orders.emptyHint', 'Your orders will appear here once you make a purchase.')}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/')}
            sx={{ borderRadius: 2, px: 4 }}
          >
            {t('cart.continueShopping', 'Continue shopping')}
          </Button>
        </Paper>
      ) : (
        <Box>
          {orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              lng={lng}
              t={t}
              onCancel={(o) => setCancelTarget(o)}
              onResumePayment={(o) => resumePayment(o.id)}
              isResuming={isResumingPayment}
            />
          ))}

          <CancelOrderDialog
            open={Boolean(cancelTarget)}
            order={cancelTarget}
            onClose={() => setCancelTarget(null)}
            onConfirm={handleCancelConfirm}
            isSubmitting={cancelMutation.isPending}
          />

          {lastPage > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={lastPage}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                shape="rounded"
                size="large"
              />
            </Box>
          )}
        </Box>
      )}
    </Container>
  );
};

export default Orders;
